<template>
  <div class="element-admin-page">
    <!-- 顶部标题区：说明这篇文章要解决什么问题 -->
    <header class="page-hero">
      <div class="hero-main">
        <h1>同样用 Element Plus，为什么后台总有「模板味」？</h1>
        <p>
          筛选能查、表格能翻页、弹窗能保存，功能全都做完了，页面却总觉得没有重点。
          问题往往不在单个组件，而在<b>组件之间的关系还没有被设计</b>。
          这篇用一个用户管理页，把布局、表格和主题重新接起来。
        </p>
        <div class="hero-tags">
          <span class="hero-tag">Vue 3</span>
          <span class="hero-tag">Element Plus</span>
          <span class="hero-tag">后台布局</span>
          <span class="hero-tag">表格设计</span>
          <span class="hero-tag">主题变量</span>
        </div>
      </div>
      <div class="hero-aside">
        <div class="aside-title">原文信息</div>
        <div class="aside-url">juejin.cn/post/7690405198255325234</div>
        <div class="aside-note">
          作者 Liora_Yvonne · 2026-09-28 发布 · 阅读约 12 分钟
        </div>
      </div>
    </header>

    <div class="page-body">
      <!-- 左侧主内容：教程正文 -->
      <main class="doc-main">
        <!-- 开篇：同一张页面的两种写法对比 -->
        <section class="doc-section doc-section--intro" id="overview">
          <div class="section-head">
            <span class="section-index">✓</span>
            <div class="section-titles">
              <h2>一句话总结</h2>
              <p>组件都没坏，坏的是组件之间的关系没有被设计。</p>
            </div>
          </div>
          <div class="section-content">
            <p class="doc-paragraph">
              查询是主按钮、新增也是主按钮，每一行还摆着三四个蓝色操作；姓名、账号、状态、时间一样醒目；
              筛选一个框、表格一个框、外面再套一个框。单独看每个组件都没问题，放在一起，页面就没有了重点。
            </p>

            <div class="before-after">
              <div class="ba-col ba-col--before">
                <div class="ba-head">调整前</div>
                <ul class="ba-list">
                  <li>查询和新增挤在同一排，都穿主色</li>
                  <li>每行三四个蓝色按钮平铺</li>
                  <li>姓名、账号各占一列，来回扫视</li>
                  <li>卡片阴影 + 外边框 + 纵向网格 + 斑马纹</li>
                </ul>
              </div>
              <div class="ba-col ba-col--after">
                <div class="ba-head">调整后</div>
                <ul class="ba-list">
                  <li>新增回页头，查询留在筛选区</li>
                  <li>高频操作直出，低频收进「更多」</li>
                  <li>姓名与账号合并为「用户」，格内分主次</li>
                  <li>只留横向分隔与 Hover，层级交给间距</li>
                </ul>
              </div>
            </div>

            <p class="doc-paragraph">
              两张图用的是同一组数据，姓名、账号、角色、状态、创建时间一个没少，
              查询、新增、编辑、分配角色、重置密码这些入口也全都保留。
              变的只有四件事：<b>位置、合并、收敛、层级</b>。
            </p>

            <div class="tip-bar tip-bar--warn">
              <i class="el-icon-warning tip-icon" />
              <span>
                原文说明：这组对比图是为文章制作的 Vue 3 + Element Plus 教学演示，使用虚构数据，
                并非某个真实项目的改版前后截图；示例里的操作频率也是假设值。
              </span>
            </div>
          </div>
        </section>

        <!-- 六个核心章节 -->
        <section
          v-for="chapter in chapters"
          :key="chapter.id"
          :id="chapter.id"
          class="doc-section"
        >
          <div class="section-head">
            <span class="section-index">{{ chapter.index }}</span>
            <div class="section-titles">
              <h2>{{ chapter.title }}</h2>
              <p v-if="chapter.desc">{{ chapter.desc }}</p>
            </div>
          </div>

          <div class="section-content">
            <!-- 普通段落 -->
            <p
              v-for="(paragraph, pIndex) in chapter.paragraphs || []"
              :key="`p-${pIndex}`"
              class="doc-paragraph"
              v-html="paragraph"
            />

            <!-- 要点列表 -->
            <ul v-if="chapter.points" class="point-list">
              <li v-for="(point, pointIndex) in chapter.points" :key="pointIndex">
                <i class="el-icon-check point-icon" />
                <span v-html="point" />
              </li>
            </ul>

            <!-- 操作分层示意 -->
            <div v-if="chapter.actionLevels" class="level-grid">
              <div
                v-for="(level, levelIndex) in chapter.actionLevels"
                :key="levelIndex"
                class="level-card"
                :class="`level-card--${level.tone}`"
              >
                <div class="level-head">
                  <span class="level-name">{{ level.name }}</span>
                  <span class="level-badge">{{ level.badge }}</span>
                </div>
                <div class="level-items">{{ level.items }}</div>
                <div class="level-reason">{{ level.reason }}</div>
              </div>
            </div>

            <!-- 代码 / 页面效果 双 Tab（左侧代码带一键复制，右侧真实渲染） -->
            <div v-if="chapter.code" class="demo-tabs">
              <div class="demo-tabs__bar">
                <div class="demo-tabs__nav">
                  <button
                    v-for="tab in demoTabs"
                    :key="tab.key"
                    type="button"
                    class="demo-tab"
                    :class="{ 'is-active': activeTab[chapter.id] === tab.key }"
                    @click="switchTab(chapter.id, tab.key)"
                  >
                    <i :class="tab.icon" class="demo-tab__icon" />
                    {{ tab.label }}
                  </button>
                </div>
                <el-button
                  v-if="activeTab[chapter.id] === 'code'"
                  type="text"
                  size="mini"
                  class="copy-button"
                  @click="copyText(chapter.code, chapter.id)"
                >
                  {{ copiedKey === chapter.id ? '已复制' : '复制代码' }}
                </el-button>
              </div>

              <!-- Tab 一：源代码 -->
              <div v-show="activeTab[chapter.id] === 'code'" class="demo-tabs__panel">
                <div class="code-label">{{ chapter.codeLabel || '代码' }}</div>
                <pre class="code-block code-block--inline"><code>{{ chapter.code }}</code></pre>
              </div>

              <!-- Tab 二：页面效果（真实渲染 Element 组件） -->
              <div v-show="activeTab[chapter.id] === 'preview'" class="demo-tabs__panel">
                <div class="code-label">页面效果 · {{ chapter.codeLabel || '' }}（真实渲染）</div>
                <div class="preview-stage">
                  <!-- 01 按钮位置：页头 + 筛选区 + 行内操作分层 -->
                  <div v-if="chapter.id === 'button-position'" class="demo-page">
                    <header class="demo-page__header">
                      <div>
                        <h1>用户管理</h1>
                        <p>管理团队账号、关联角色与访问状态</p>
                      </div>
                      <el-button type="primary" size="small">新增用户</el-button>
                    </header>

                    <div class="demo-page__filters">
                      <el-input
                        v-model="demoUsers.keyword"
                        size="small"
                        placeholder="姓名 / 账号"
                        class="filter-item"
                      />
                      <el-select v-model="demoUsers.status" size="small" placeholder="状态" class="filter-item">
                        <el-option label="启用" value="启用" />
                        <el-option label="停用" value="停用" />
                      </el-select>
                      <el-button size="small">查询</el-button>
                      <el-button size="small">重置</el-button>
                    </div>

                    <el-table :data="filteredUsers" size="small" class="admin-table demo-preview-table">
                      <el-table-column prop="name" label="用户" min-width="140" />
                      <el-table-column prop="role" label="角色" width="110" />
                      <el-table-column prop="status" label="状态" width="90" />
                      <el-table-column label="操作" width="140">
                        <template>
                          <el-button type="text" size="mini">编辑</el-button>
                          <el-button type="text" size="mini" class="op-more">更多</el-button>
                        </template>
                      </el-table-column>
                    </el-table>
                  </div>

                  <!-- 02 表格阅读顺序：合并列，格内分主次 -->
                  <div v-else-if="chapter.id === 'table-reading'" class="demo-page">
                    <el-table :data="demoUsers.list" size="small" class="admin-table demo-preview-table">
                      <el-table-column label="用户" min-width="160">
                        <template slot-scope="{ row }">
                          <div class="user-identity">
                            <strong>{{ row.name }}</strong>
                            <span>{{ row.account }}</span>
                          </div>
                        </template>
                      </el-table-column>
                      <el-table-column prop="role" label="角色" width="110" />
                      <el-table-column prop="status" label="状态" width="90" />
                      <el-table-column prop="amount" label="金额" width="110" align="right" />
                      <el-table-column prop="createdAt" label="创建时间" width="170" class-name="col-time" />
                    </el-table>
                    <div class="preview-note">
                      <i class="el-icon-view" />
                      对比要点：姓名与账号同格分主次；金额右对齐便于比位数；时间用等宽数字。
                    </div>
                  </div>

                  <!-- 03 边框斑马纹留白：只留横向分隔与 Hover -->
                  <div v-else-if="chapter.id === 'borders-whitespace'" class="demo-page">
                    <div class="preview-switch">
                      <span class="preview-switch__label">装饰开关：</span>
                      <el-checkbox v-model="demoUsers.decor.border" size="small">外边框</el-checkbox>
                      <el-checkbox v-model="demoUsers.decor.stripe" size="small">斑马纹</el-checkbox>
                      <el-checkbox v-model="demoUsers.decor.shadow" size="small">卡片阴影</el-checkbox>
                      <el-checkbox v-model="demoUsers.decor.grid" size="small">纵向网格</el-checkbox>
                    </div>
                    <div
                      class="demo-decor"
                      :class="[
                        { 'has-border': demoUsers.decor.border },
                        { 'has-shadow': demoUsers.decor.shadow }
                      ]"
                    >
                      <el-table
                        :data="demoUsers.list"
                        size="small"
                        :border="demoUsers.decor.grid"
                        :stripe="demoUsers.decor.stripe"
                        class="admin-table demo-preview-table"
                      >
                        <el-table-column prop="name" label="姓名" min-width="110" />
                        <el-table-column prop="account" label="账号" min-width="120" />
                        <el-table-column prop="status" label="状态" width="100">
                          <template slot-scope="{ row }">
                            <span :class="row.status === '启用' ? 'status-on' : 'status-off'">
                              {{ row.status }}
                            </span>
                          </template>
                        </el-table-column>
                        <el-table-column prop="createdAt" label="创建时间" width="170" />
                      </el-table>
                    </div>
                    <div class="preview-note">
                      <i class="el-icon-view" />
                      全部关掉后只剩横向分隔与 Hover —— 装饰退场，数据才是重点。
                    </div>
                  </div>

                  <!-- 04 主题色阶 -->
                  <div v-else-if="chapter.id === 'primary-color'" class="demo-page">
                    <div class="preview-switch">
                      <span class="preview-switch__label">主色方案：</span>
                      <el-radio-group v-model="demoUsers.theme" size="small">
                        <el-radio-button label="default">默认蓝</el-radio-button>
                        <el-radio-button label="partial">只改 primary</el-radio-button>
                        <el-radio-button label="full">完整色阶</el-radio-button>
                      </el-radio-group>
                    </div>
                    <div class="theme-demo" :class="`theme-demo--${demoUsers.theme}`">
                      <div class="theme-demo__row">
                        <el-button type="primary" size="small">主按钮</el-button>
                        <el-button type="primary" size="small" plain>朴素按钮</el-button>
                        <el-button type="primary" size="small" disabled>禁用</el-button>
                      </div>
                      <div class="theme-demo__row theme-demo__row--light">
                        <span class="light-swatch">浅色背景 + 白字</span>
                        <el-tag size="small" type="success" effect="light">选中标签</el-tag>
                      </div>
                      <div class="color-scale">
                        <span
                          v-for="scale in colorScales"
                          :key="scale.key"
                          class="color-scale__item"
                          :style="{ background: scale.value }"
                        >
                          {{ scale.name }}
                        </span>
                      </div>
                    </div>
                    <div class="preview-note">
                      <i class="el-icon-view" />
                      切到「只改 primary」可以看到 Hover、浅色背景、选中态没有完全跟着走 —— 这正是原文指出的问题。
                    </div>
                  </div>

                  <!-- 05 深色主题变量 -->
                  <div v-else-if="chapter.id === 'dark-theme'" class="demo-page">
                    <div class="preview-switch">
                      <span class="preview-switch__label">主题：</span>
                      <el-switch
                        v-model="demoUsers.darkMode"
                        size="small"
                        active-text="深色"
                        inactive-text="浅色"
                      />
                    </div>
                    <div class="theme-surface" :data-theme="demoUsers.darkMode ? 'dark' : 'light'">
                      <div class="theme-card">
                        <div class="theme-card__title">新增用户</div>
                        <el-input v-model="demoUsers.form.name" size="small" placeholder="姓名" class="theme-field" />
                        <el-select
                          v-model="demoUsers.form.role"
                          size="small"
                          placeholder="角色"
                          class="theme-field"
                          popper-class="theme-popper"
                        >
                          <el-option label="管理员" value="管理员" />
                          <el-option label="编辑" value="编辑" />
                        </el-select>
                        <el-button type="primary" size="small" class="theme-field">提交</el-button>
                      </div>
                      <div class="theme-card theme-card--flat">
                        <div class="theme-hint">
                          下拉浮层由 <code>append-to-body</code> 传送到 body，若变量只写在容器上，它就会取不到 —— 展开下拉看看。
                        </div>
                      </div>
                    </div>
                    <div class="preview-note">
                      <i class="el-icon-view" />
                      浅色 / 深色切换：表面、文字、分隔线各用一套变量，切换只换值不改结构。
                    </div>
                  </div>

                  <!-- 兜底：未配置演示的章节 -->
                  <div v-else class="preview-empty">该章节暂未提供效果演示。</div>
                </div>
              </div>
            </div>

            <!-- 对照表 -->
            <div v-if="chapter.table" class="table-card">
              <div
                v-for="(row, rowIndex) in chapter.table"
                :key="rowIndex"
                class="table-row"
              >
                <div class="table-key">{{ row.key }}</div>
                <div class="table-value" v-html="row.value" />
              </div>
            </div>

            <!-- 提示条 -->
            <div v-if="chapter.tip" class="tip-bar">
              <i class="el-icon-info tip-icon" />
              <span>{{ chapter.tip }}</span>
            </div>

            <!-- 警示条 -->
            <div v-if="chapter.warn" class="tip-bar tip-bar--warn">
              <i class="el-icon-warning tip-icon" />
              <span>{{ chapter.warn }}</span>
            </div>
          </div>
        </section>

        <!-- 第七章：规则收拢（职责分工表） -->
        <section id="layer-rules" class="doc-section">
          <div class="section-head">
            <span class="section-index">07</span>
            <div class="section-titles">
              <h2>做到第二张页面，就该把这些规则收起来</h2>
              <p>否则用户页 24px、角色页 20px、菜单页再加一层阴影，同一套组件库又拼出三种风格。</p>
            </div>
          </div>
          <div class="section-content">
            <p class="doc-paragraph">
              第一张页面做好后，最容易发生的事是：下一张页面复制一份 CSS，再改几个数字。
              几轮之后，同一条规范就有了三个版本。更稳妥的做法是按职责拆开放置：
            </p>

            <div class="duty-table">
              <div class="duty-head">
                <span>放在哪里</span>
                <span>维护什么</span>
              </div>
              <div v-for="(duty, dutyIndex) in duties" :key="dutyIndex" class="duty-row">
                <span class="duty-where">{{ duty.where }}</span>
                <span class="duty-what" v-html="duty.what" />
              </div>
            </div>

            <div class="tip-bar tip-bar--warn">
              <i class="el-icon-warning tip-icon" />
              <span>
                这种分工不等于立刻造一个「万能表格」，把所有插槽、按钮和条件塞进一份 JSON。
                先复用已经稳定的样式和布局，业务差异继续留在页面里，维护起来通常更直观。
              </span>
            </div>
          </div>
        </section>

        <!-- 收尾：五条检查清单 -->
        <section class="doc-section doc-section--summary" id="checklist">
          <div class="section-head">
            <span class="section-index">✓</span>
            <div class="section-titles">
              <h2>下次接到后台页面，先检查这五件事</h2>
              <p>Element Plus 提供了可靠的零件，最终长什么样，取决于我们怎么安排这些零件。</p>
            </div>
          </div>
          <div class="section-content">
            <ol class="check-list">
              <li v-for="(item, itemIndex) in checklist" :key="itemIndex">
                <span class="check-index">{{ itemIndex + 1 }}</span>
                <span class="check-text">{{ item }}</span>
              </li>
            </ol>

            <div class="final-note">
              <p class="doc-paragraph">
                如果要先从自己的后台挑一处改，建议先看那一列永远摆满蓝色文字的「操作」——
                你可能会发现，真正每天都在点的，只有其中一两个。
              </p>
              <p class="doc-paragraph doc-paragraph--last">
                文章末尾还留了一个延伸方向：页面优化之后，可以继续把背后的业务链路补上
                （新增怎么入库、筛选分页怎么接、分配角色与重置密码怎么鉴权），
                作者预告了下一篇将用 NestJS + TypeScript 沿着这张用户管理页做出来。
              </p>
            </div>
          </div>
        </section>
      </main>

      <!-- 右侧目录：锚点导航 + 核心结论 -->
      <aside class="doc-aside">
        <div class="toc-card">
          <div class="toc-title">目录</div>
          <ul class="toc-list">
            <li
              class="toc-item"
              :class="{ 'is-active': activeId === 'overview' }"
              @click="scrollTo('overview')"
            >
              <span class="toc-index">✓</span>
              <span class="toc-text">一句话总结</span>
            </li>
            <li
              v-for="chapter in chapters"
              :key="`toc-${chapter.id}`"
              class="toc-item"
              :class="{ 'is-active': activeId === chapter.id }"
              @click="scrollTo(chapter.id)"
            >
              <span class="toc-index">{{ chapter.index }}</span>
              <span class="toc-text">{{ chapter.tocTitle || chapter.title }}</span>
            </li>
            <li
              class="toc-item"
              :class="{ 'is-active': activeId === 'layer-rules' }"
              @click="scrollTo('layer-rules')"
            >
              <span class="toc-index">07</span>
              <span class="toc-text">规则收拢与职责分工</span>
            </li>
            <li
              class="toc-item"
              :class="{ 'is-active': activeId === 'checklist' }"
              @click="scrollTo('checklist')"
            >
              <span class="toc-index">✓</span>
              <span class="toc-text">五条检查清单</span>
            </li>
          </ul>
        </div>

        <div class="toc-card">
          <div class="toc-title">核心结论</div>
          <div class="fact-row">
            <span>问题根因</span>
            <strong>组件间关系未设计</strong>
          </div>
          <div class="fact-row">
            <span>四项改动</span>
            <strong>位置 / 合并 / 收敛 / 层级</strong>
          </div>
          <div class="fact-row">
            <span>主按钮</span>
            <strong>按使用频率决定</strong>
          </div>
          <div class="fact-row">
            <span>主题色</span>
            <strong>只改 primary 不够</strong>
          </div>
          <div class="fact-row">
            <span>深色主题</span>
            <strong>变量放 html / :root</strong>
          </div>
          <div class="fact-row">
            <span>规范沉淀</span>
            <strong>第二张页面就抽离</strong>
          </div>
        </div>

        <div class="toc-card">
          <div class="toc-title">参考项目</div>
          <a
            class="repo-link"
            href="https://github.com/liangy0323/ly-fullstack"
            target="_blank"
            rel="noopener"
          >
            ly-fullstack
          </a>
          <div class="aside-note aside-note--dark">
            原文作者维护的开源后台，用户管理页就是按「主题 / 组件适配 / 列表公共样式 / 业务页面」四层分工实现的。
          </div>
          <ul class="repo-file-list">
            <li>
              <a
                href="https://github.com/liangy0323/ly-fullstack/blob/main/apps/admin/src/views/system/user/index.vue"
                target="_blank"
                rel="noopener"
              >
                用户管理页 index.vue
              </a>
            </li>
            <li>
              <a
                href="https://github.com/liangy0323/ly-fullstack/blob/main/apps/admin/src/assets/styles/modules/crud.scss"
                target="_blank"
                rel="noopener"
              >
                公共 CRUD 布局 crud.scss
              </a>
            </li>
            <li>
              <a
                href="https://github.com/liangy0323/ly-fullstack/blob/main/apps/admin/src/assets/element-plus/modules/table.scss"
                target="_blank"
                rel="noopener"
              >
                表格适配 table.scss
              </a>
            </li>
          </ul>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
// 六个核心章节数据：正文全部由这份数据驱动渲染。
const CHAPTERS = [
  {
    id: 'button-position',
    index: '01',
    tocTitle: '先把按钮放对位置',
    title: '先把按钮放对位置，再考虑它是什么颜色',
    desc: '把查询、重置、新增、导出放进同一排开发起来很顺手，但它们并不属于同一件事。',
    paragraphs: [
      '查询和重置控制的是<b>当前列表的筛选条件</b>；新增是在<b>创建一个新的业务对象</b>。位置相邻，却不意味着职责相同。',
      '所以先定骨架：页头放页面标题与页面级主操作（新增），筛选区只放筛选字段与查询/重置，然后是表格，最后是分页。进入页面先知道自己在哪里，接着可以新增，也可以缩小列表范围，最后处理具体的一行。'
    ],
    codeLabel: '页面骨架',
    code: `<section class="user-page">
  <header class="user-page__header">
    <div>
      <h1>用户管理</h1>
      <p>管理团队账号、关联角色与访问状态</p>
    </div>
    <el-button type="primary" @click="openCreate">新增用户</el-button>
  </header>

  <form class="user-page__filters" @submit.prevent="search">
    <!-- 带明确标签的筛选字段 -->
    <el-button native-type="submit">查询</el-button>
    <el-button @click="reset">重置</el-button>
  </form>

  <el-table class="admin-table" :data="users">
    <!-- 业务列 -->
  </el-table>

  <footer class="user-page__pagination">
    <!-- 分页 -->
  </footer>
</section>`,
    paragraphsAfter: true,
    actionLevels: [
      {
        name: '页面级主操作',
        badge: '实心主按钮',
        tone: 'primary',
        items: '新增用户',
        reason: '创建业务对象，是这张页面最重要的一件事，保留最高颜色权重。'
      },
      {
        name: '筛选区操作',
        badge: '降低颜色权重',
        tone: 'plain',
        items: '查询 / 重置',
        reason: '紧挨筛选字段，位置已经表达了用途，颜色不必再抢。'
      },
      {
        name: '行内高频',
        badge: '直接显示',
        tone: 'plain',
        items: '编辑',
        reason: '假设它最常用，所以留在表格里一眼可见。'
      },
      {
        name: '行内低频',
        badge: '收进「更多」',
        tone: 'muted',
        items: '分配角色 / 重置密码',
        reason: '低频操作收起来，避免每行都是蓝色文字。'
      }
    ],
    warn: '这不是「每个页面只能有一个主按钮」的硬规则。如果页面是交易流水查询，用户大部分时间都在反复查数据，查询完全可以成为最突出的操作。先确认用户进来最常做什么，才能决定谁该抢眼。同理，若客服每天都在重置密码，把它收进「更多」只会增加操作次数。'
  },
  {
    id: 'table-reading',
    index: '02',
    tocTitle: '表格先看阅读顺序',
    title: '表格的质感，先从「哪些字段应该一起读」开始',
    desc: '数据库里有几列，不代表界面就必须有几列。',
    paragraphs: [
      '姓名和账号通常是在共同回答「这是谁」。把它们分开，用户要来回扫两列；合在一起，姓名负责识别，账号负责消除重名歧义。同样的信息，现在有了<b>阅读顺序</b>——不用加插画，也不需要给每个人配一个随机颜色的头像。'
    ],
    codeLabel: '合并列 + 主次样式',
    code: `<el-table-column label="用户" min-width="220">
  <template #default="{ row }">
    <div class="user-identity">
      <strong>{{ row.displayName || row.username }}</strong>
      <span>{{ row.username }}</span>
    </div>
  </template>
</el-table-column>

<style>
.user-identity {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.user-identity strong {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.user-identity span {
  color: var(--text-secondary);
  font-size: 12px;
}
</style>`,
    points: [
      '<strong>让状态保留文字。</strong>「启用」「停用」即使加了颜色，也要能直接读出来；颜色辅助扫描，文字负责明确含义，不要为了整齐把所有状态都刷成品牌色',
      '<strong>按数据类型决定对齐。</strong>金额通常右对齐，更容易比较位数；姓名和描述左对齐，方便连续阅读；时间可以用 <code>font-variant-numeric: tabular-nums</code> 让数字宽度一致',
      '<strong>不要为了看起来规整，把所有列都居中</strong>'
    ],
    warn: '合并也有边界：如果用户经常单独按账号排序、批量复制账号，独立列可能更合适。视觉上更紧凑，不应牺牲实际任务。'
  },
  {
    id: 'borders-whitespace',
    index: '03',
    tocTitle: '边框、斑马纹与留白',
    title: '边框、斑马纹和留白，都应该帮助读数据',
    desc: '装饰一旦开始争抢注意力，数据本身就被压下去了。',
    paragraphs: [
      '<code>border</code>、<code>stripe</code> 都有适用场景。宽表格里跨很多列比对数据，清晰的网格或斑马纹可能很有用。',
      '但只有几个字段的用户列表，如果同时出现<b>卡片阴影、外边框、纵向网格、斑马纹</b>，再给每个状态套一个标签，装饰本身就会占掉不少注意力。可以只保留横向分隔与 Hover 反馈，把纵向关系交给对齐和间距。',
      'Element Plus 表格提供了可覆盖的 CSS 变量。下面的写法放在<b>全局样式文件</b>，并通过 <code>.admin-table</code> 限定到采用这套规范的表格。'
    ],
    codeLabel: '表格变量与单元格间距（全局样式）',
    code: `.admin-table {
  --el-table-header-bg-color: var(--surface);
  --el-table-tr-bg-color: var(--surface);
  --el-table-row-hover-bg-color: var(--surface-hover);
  --el-table-border-color: var(--line);
  --el-table-text-color: var(--text-secondary);
  --el-table-header-text-color: var(--text-primary);
}

.admin-table .el-table__cell {
  padding: 12px 0;
}

.admin-table .cell {
  padding-inline: 24px;
}`,
    points: [
      '变量处理颜色，后两个选择器处理单元格的空间',
      '它们依赖组件内部类名，<b>升级组件库时要一起检查</b>；不要把这些覆盖复制到每个业务页面里',
      '如果放进 Vue 的 <code>scoped</code> 样式，内部节点可能无法被普通后代选择器命中；确实需要局部覆盖时，再按 Vue 的规则使用 <code>:deep()</code>。选一种明确的维护方式，别让同一张表被多个地方轮流覆盖'
    ],
    tip: '示例把页头、筛选字段和表格内容的起始位置统一到 24px，视觉上就有了一条稳定的对齐线。这里的 12px、24px 都只是这张页面的取值——高频录入和密集检索的后台需要更紧凑的行高，长文本多的列表又需要允许换行。大留白不是高级感的通行证，多看一屏数据有时比多留一块空白更重要。'
  },
  {
    id: 'primary-color',
    index: '04',
    tocTitle: '只改主色为什么不够',
    title: '只改 --el-color-primary，为什么还不够？',
    desc: '按钮正常时变绿了，Hover、浅色背景、选中态却没有完全跟着走。',
    paragraphs: [
      '原因是组件并不只消费一个主色。Element Plus 的主题还包含 <code>primary-light-*</code>、<code>primary-dark-2</code> 等色阶；<b>只覆盖基础变量，不会自动重算已经生成的其他色阶</b>。官方主题文档提供了 Sass 定制和 CSS 变量两种路径。',
      '如果项目主色相对固定，可以在官方 Sass 变量模块首次加载前配置：'
    ],
    codeLabel: 'Sass 定制入口 styles/element-theme.scss',
    code: `@forward 'element-plus/theme-chalk/src/common/var.scss' with (
  $colors: (
    'primary': (
      'base': #087f5b,
    ),
  )
);

// 全量样式引入示例；按需引入项目不要再加载这一行。
@use 'element-plus/theme-chalk/src/index.scss' as *;`,
    points: [
      '这份入口应<b>替代</b>原来的预编译 <code>element-plus/dist/index.css</code>，而不是在它后面再叠一套',
      '按需引入时，需要让定制变量<b>先于</b>对应组件的 Sass 加载',
      '需要运行时换品牌色也可以用 CSS 变量，但要<b>同时维护相关色阶</b>'
    ],
    warn: '还有一个容易忽略的点：自动生成了色阶，不等于所有组合都适合阅读。尤其是浅色 Hover 背景配白字，要实际检查对比度，必要时单独调整按钮交互态。'
  },
  {
    id: 'dark-theme',
    index: '05',
    tocTitle: '深色主题的变量位置',
    title: '深色主题暴露的问题，往往来自变量放错了地方',
    desc: '业务代码到处写着 #fff、#606266，加深色主题就变成了到处打补丁。',
    paragraphs: [
      '第一步不是写深色值，而是把页面需要的颜色<b>按用途命名</b>：正文色、表面色、分隔线，各自一个变量。这样深浅两套主题只需要替换变量值。'
    ],
    codeLabel: '按用途命名 + 双主题变量',
    code: `:root {
  --surface: #fff;
  --surface-hover: #f2f6f4;
  --text-primary: #202b33;
  --text-secondary: #5c6872;
  --line: #e3e8ec;
}

html[data-theme='dark'] {
  color-scheme: dark;
  --surface: #1c242c;
  --surface-hover: #27333b;
  --text-primary: #e8edf0;
  --text-secondary: #b4c0c9;
  --line: #35434e;
}`,
    table: [
      {
        key: '细节一',
        value: '<b>弱分隔线和输入框边界不要机械共用一种浅灰。</b>分隔线可以退后，输入框还得让人知道哪里能点、当前焦点在哪里。整页淡到看不清，并不会更精致。'
      },
      {
        key: '细节二',
        value: '<b>全局主题变量应放在 <code>html</code> 或 <code>:root</code>，不能只放在 <code>.user-page</code>。</b>例如 Select 的下拉浮层默认会被传送到页面其他位置，它不再是业务容器的后代，也就无法继承只定义在容器上的变量。'
      }
    ],
    tip: '「输入框已经变深色，下拉菜单还是白色」的一个常见排查方向就是上面的细节二。还要检查浮层实际消费的 --el-bg-color-overlay 等变量，不能只改输入框背景。页面截图能确认大体层次，但验证主题还得亲自打开下拉菜单、移动鼠标、用 Tab 切换焦点，再看看禁用和空数据状态——真正不协调的颜色，常常藏在这些时刻。'
  },
  {
    id: 'codify',
    index: '06',
    tocTitle: '规范要收起来',
    title: '做到第二张页面，就该把这些规则收起来',
    desc: '先复用已经稳定的样式和布局，业务差异继续留在页面里。',
    paragraphs: [
      '第一张页面做好后，最容易发生的事是：下一张页面<b>复制一份 CSS，再改几个数字</b>。几轮之后，用户页是 24px，角色页是 20px，菜单页又补了一层阴影——同一套组件库，再次拼出了三种风格。',
      '更稳妥的做法是按<b>职责</b>把规则分开维护，让每一类规则只有一个落点。'
    ],
    tip: '下一节的职责分工表把这件事拆成了四层，可以直接对照自己的项目检查。'
  }
]

// 职责分工表：规则应该放在哪里
const DUTIES = [
  { where: '主题文件', what: '表面、文字、边框、状态与交互颜色' },
  { where: '组件适配文件', what: 'Element Plus 变量与必要的内部样式覆盖' },
  { where: '列表页公共样式', what: '页头、筛选、表格、分页的布局与间距' },
  { where: '业务页面', what: '字段内容、权限条件、操作顺序与数据行为' }
]

// 收尾检查清单
const CHECKLIST = [
  '最重要的操作，能不能一眼找到？',
  '本来属于同一个对象的信息，是否被拆得太散？',
  '边框、标签和颜色，是在帮助阅读，还是彼此争抢注意力？',
  '换到另一张业务页面，间距和交互规则是否仍然一致？',
  '打开浮层、切换主题、出现空数据以后，页面是否还完整？'
]

// 演示区共用的用户数据
const DEMO_USERS = [
  { name: '林知远', account: 'linzhiyuan', role: '管理员', status: '启用', amount: '12,800.00', createdAt: '2026-09-12 09:41' },
  { name: '沈亦舟', account: 'shenyizhou', role: '编辑', status: '启用', amount: '860.50', createdAt: '2026-09-18 14:02' },
  { name: '周砚清', account: 'zhouyanqing', role: '访客', status: '停用', amount: '9,240.00', createdAt: '2026-09-21 17:35' },
  { name: '许照微', account: 'xuzhaowei', role: '编辑', status: '启用', amount: '76.00', createdAt: '2026-09-25 08:19' }
]

// 主题色阶对照（用于第 04 章演示主色未重算色阶的问题）
const COLOR_SCALES = [
  { key: 'base', name: 'primary', value: '#087f5b' },
  { key: 'light-3', name: 'light-3', value: '#79b9a5' },
  { key: 'light-5', name: 'light-5', value: '#b6d8ce' },
  { key: 'light-7', name: 'light-7', value: '#d8ebe5' },
  { key: 'light-9', name: 'light-9', value: '#eef7f4' },
  { key: 'dark-2', name: 'dark-2', value: '#06664a' }
]

export default {
  name: 'PcOnlineTeachingElementAdminPolish',

  data() {
    return {
      // 章节正文数据
      chapters: CHAPTERS,
      // 职责分工数据
      duties: DUTIES,
      // 检查清单数据
      checklist: CHECKLIST,
      // 代码 / 页面效果 两个 Tab
      demoTabs: [
        { key: 'code', label: '代码', icon: 'el-icon-document' },
        { key: 'preview', label: '页面效果', icon: 'el-icon-view' }
      ],
      // 各章节当前选中的 Tab（默认都停在代码页）
      activeTab: CHAPTERS.reduce((acc, chapter) => {
        acc[chapter.id] = 'code'
        return acc
      }, {}),
      // 主题色阶
      colorScales: COLOR_SCALES,
      // 演示区交互状态
      demoUsers: {
        keyword: '',
        status: '',
        list: DEMO_USERS,
        theme: 'default',
        darkMode: false,
        form: { name: '', role: '' },
        decor: {
          border: false,
          stripe: false,
          shadow: false,
          grid: false
        }
      },
      // 当前高亮的目录项
      activeId: 'overview',
      // 最近一次复制的目标标识，用于按钮文案反馈
      copiedKey: '',
      // 复制反馈的复位定时器
      copyTimer: null
    }
  },

  computed: {
    // 第 01 章演示区：按关键字与状态过滤
    filteredUsers() {
      const keyword = this.demoUsers.keyword.trim()
      const status = this.demoUsers.status

      return this.demoUsers.list.filter(user => {
        const matchKeyword = !keyword ||
          user.name.includes(keyword) ||
          user.account.includes(keyword)
        const matchStatus = !status || user.status === status
        return matchKeyword && matchStatus
      })
    }
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll, true)
    this.handleScroll()
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll, true)
    if (this.copyTimer) {
      clearTimeout(this.copyTimer)
      this.copyTimer = null
    }
  },

  methods: {
    // 切换某个章节的代码 / 页面效果 Tab
    switchTab(chapterId, tabKey) {
      this.$set(this.activeTab, chapterId, tabKey)
    },

    // 点击目录项时平滑滚动到对应章节
    scrollTo(id) {
      const target = document.getElementById(id)
      if (!target) return
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      this.activeId = id
    },

    // 滚动时根据章节位置更新目录高亮
    handleScroll() {
      const offset = 140
      const sections = [
        { id: 'overview' },
        ...this.chapters,
        { id: 'layer-rules' },
        { id: 'checklist' }
      ]
      let current = this.activeId

      sections.forEach(section => {
        const el = document.getElementById(section.id)
        if (!el) return
        if (el.getBoundingClientRect().top <= offset) {
          current = section.id
        }
      })

      if (current !== this.activeId) {
        this.activeId = current
      }
    },

    // 复制文本到剪贴板，并给出短暂的按钮反馈
    copyText(text, key) {
      const done = () => {
        this.copiedKey = key
        if (this.copyTimer) clearTimeout(this.copyTimer)
        this.copyTimer = setTimeout(() => {
          this.copiedKey = ''
        }, 1800)
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done))
        return
      }

      this.fallbackCopy(text, done)
    },

    // 兼容不支持 clipboard API 的环境（如 http 下的部分浏览器）
    fallbackCopy(text, done) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()

      try {
        document.execCommand('copy')
        done()
      } catch (error) {
        this.$toast.warning('当前浏览器不支持自动复制，请手动选中复制')
      }

      document.body.removeChild(textarea)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/breakpoints.scss';

.element-admin-page {
  /* 撑满内容区，滚动下沉到模块内部 */
  height: 100%;
  box-sizing: border-box;
  overflow: auto;
  padding: 0;
  background: transparent;
}

// ===== 顶部标题区 =====
.page-hero {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
  padding: 24px 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, #23203d 0%, #3b3566 55%, #5a4f96 100%);
  box-shadow: 0 8px 22px rgba(35, 32, 61, 0.18);
}

.hero-main {
  min-width: 0;
  max-width: 720px;
}

.hero-main h1 {
  margin: 0 0 12px;
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0.5px;

  @include bp-middle {
    font-size: 23px;
  }

  @include bp-small {
    font-size: 22px;
  }
}

.hero-main p {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 14px;
  line-height: 1.75;

  b {
    color: #fff;
    font-weight: 600;
  }

  @include bp-middle {
    font-size: 13px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.hero-tag {
  padding: 4px 10px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
}

.hero-aside {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 360px;
  padding: 16px 18px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);

  @include bp-middle {
    width: 340px;
  }

  @include bp-small {
    width: 300px;
  }
}

.aside-title {
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.aside-url {
  color: #fff;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.7;
  word-break: break-all;

  @include bp-small {
    font-size: 12px;
  }
}

.aside-note {
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 12px;
  line-height: 1.6;
}

// ===== 主体双栏 =====
.page-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 18px;
  align-items: start;

  @include bp-middle {
    grid-template-columns: minmax(0, 1fr) 280px;
  }

  @include bp-small {
    grid-template-columns: minmax(0, 1fr) 260px;
  }
}

.doc-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// ===== 章节卡片 =====
.doc-section {
  padding: 22px 24px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  background: var(--surface-1);
  box-shadow: 0 4px 14px rgba(31, 45, 61, 0.06);
  scroll-margin-top: 90px;
}

.doc-section--intro {
  border-color: #e0dcf5;
  background: var(--surface-1);
}

.doc-section--summary {
  border-color: #d8e7d8;
  background: var(--surface-1);
}

.section-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-bottom: 16px;
  border-bottom: 1px dashed #e8eef6;
}

.section-index {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f0eefc;
  color: #5a4f96;
  font-size: 14px;
  font-weight: 600;

  @include bp-middle {
    width: 32px;
    height: 32px;
    font-size: 13px;
  }

  @include bp-small {
    width: 30px;
    height: 30px;
    font-size: 13px;
  }
}

.section-titles {
  min-width: 0;
}

.section-titles h2 {
  margin: 0 0 8px;
  color: #1f2d3d;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;

  @include bp-middle {
    font-size: 17px;
  }

  @include bp-small {
    font-size: 16px;
  }
}

.section-titles p {
  margin: 0;
  color: #7a8797;
  font-size: 13px;
  line-height: 1.7;

  @include bp-middle {
    font-size: 12px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.section-content {
  padding-top: 16px;
}

.doc-paragraph {
  margin: 0 0 12px;
  color: #42536a;
  font-size: 14px;
  line-height: 1.85;

  &:last-child {
    margin-bottom: 0;
  }

  code {
    padding: 2px 6px;
    border-radius: 3px;
    background: #eef3fa;
    color: #315dcf;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
  }

  b,
  strong {
    color: #1f2d3d;
    font-weight: 600;
  }

  @include bp-middle {
    font-size: 13px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.doc-paragraph--last {
  margin-bottom: 0;
}

// ===== 前后对比 =====
.before-after {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin: 4px 0 14px;

  @include bp-small {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ba-col {
  padding: 14px 16px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  background: var(--surface-1);
}

.ba-col--before {
  border-left: 3px solid #d98b8b;
}

.ba-col--after {
  border-left: 3px solid #52a552;
}

.ba-head {
  margin-bottom: 10px;
  color: #1f2d3d;
  font-size: 13px;
  font-weight: 600;
}

.ba-list {
  margin: 0;
  padding-left: 18px;
  color: #667085;
  font-size: 13px;
  line-height: 1.85;

  @include bp-small {
    font-size: 12px;
  }
}

// ===== 操作分层卡片 =====
.level-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 4px 0 14px;

  @include bp-small {
    grid-template-columns: minmax(0, 1fr);
  }
}

.level-card {
  padding: 12px 14px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  background: var(--surface-1);
}

.level-card--primary {
  border-left: 3px solid #5a4f96;
  background: var(--surface-1);
}

.level-card--plain {
  border-left: 3px solid #9aa8bd;
}

.level-card--muted {
  border-left: 3px solid #d5dbe4;
  background: var(--surface-1);
}

.level-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.level-name {
  color: #1f2d3d;
  font-size: 13px;
  font-weight: 600;
}

.level-badge {
  padding: 2px 8px;
  border-radius: 10px;
  background: var(--surface-2);
  color: #7a8797;
  font-size: 11px;
  white-space: nowrap;
}

.level-items {
  color: #315dcf;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
  line-height: 1.7;
  word-break: break-all;
}

.level-reason {
  margin-top: 8px;
  color: #667085;
  font-size: 12px;
  line-height: 1.7;
}

// ===== 代码 / 页面效果 双 Tab =====
.demo-tabs {
  margin: 14px 0;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  background: var(--surface-1);
  overflow: hidden;
}

.demo-tabs__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 14px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  background: var(--surface-1);
}

.demo-tabs__nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.demo-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 14px;
  border: none;
  background: transparent;
  color: #7a8797;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #5a4f96;
  }

  // 选中态下划线
  &.is-active {
    color: #5a4f96;
    font-weight: 600;

    &::after {
      content: '';
      position: absolute;
      left: 10px;
      right: 10px;
      bottom: -1px;
      height: 2px;
      border-radius: 2px;
      background: #5a4f96;
    }
  }

  @include bp-small {
    padding: 10px 10px;
    font-size: 12px;
  }
}

.demo-tab__icon {
  font-size: 13px;
}

.demo-tabs__panel {
  padding: 12px 14px;
  background: var(--surface-1);
}

.code-label {
  margin-bottom: 8px;
  color: #7a8797;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.copy-button {
  flex: none;
  padding: 0;
  color: #5a4f96;
  font-size: 12px;
}

.code-block {
  margin: 0;
  padding: 12px 14px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  background: var(--surface-1);
  color: #2c3e50;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-x: auto;

  @include bp-small {
    font-size: 12px;
  }
}

.code-block--inline {
  background: var(--surface-1);
}

// ===== 页面效果演示区 =====
.preview-stage {
  padding: 16px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  background: var(--surface-1);
}

.demo-page {
  min-width: 0;
}

// 演示用迷你页面骨架
.demo-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px dashed #e8eef6;

  h1 {
    margin: 0 0 4px;
    color: #1f2d3d;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #909399;
    font-size: 12px;
  }
}

.demo-page__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
}

.filter-item {
  width: 160px;

  @include bp-small {
    width: 130px;
  }
}

// 演示表格：只留横向分隔与 Hover，去掉纵向网格的存在感
.demo-preview-table {
  ::v-deep .el-table__cell {
    padding: 8px 0;
  }

  ::v-deep th {
    background: var(--surface-1);
    color: #1f2d3d;
    font-weight: 600;
  }

  ::v-deep td {
    color: #5c6872;
  }

  // 时间列用等宽数字
  ::v-deep .col-time .cell {
    font-variant-numeric: tabular-nums;
  }
}

// 姓名 + 账号同格分主次
.user-identity {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;

  strong {
    color: #202b33;
    font-size: 13px;
    font-weight: 600;
  }

  span {
    color: #909399;
    font-size: 12px;
  }
}

// 操作列的「更多」弱化，避免整列都是蓝色高亮
.op-more {
  color: #a8b6c8 !important;
}

.status-on {
  color: #52a552;
}

.status-off {
  color: #c0c4cc;
}

.preview-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 4px;
  background: #f7f5ff;
  color: #667085;
  font-size: 12px;
  line-height: 1.7;

  i {
    margin-top: 2px;
    color: #5a4f96;
  }
}

.preview-switch {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 4px;
  background: var(--surface-1);
}

.preview-switch__label {
  color: #7a8797;
  font-size: 12px;
}

// 装饰开关演示
.demo-decor {
  border-radius: 6px;
  transition: box-shadow 0.2s, border-color 0.2s;

  &.has-border {
    border: 1px solid rgba(31, 45, 61, 0.06);
    padding: 8px;
  }

  &.has-shadow {
    box-shadow: 0 4px 14px rgba(31, 45, 61, 0.14);
  }
}

// 主题色阶演示
.theme-demo {
  // 默认蓝：完整色阶
  .color-scale__item {
    background: #409eff;
  }
}

// 只改 primary：按钮变了，但浅色背景 / 选中态仍停留在旧色阶
.theme-demo--partial {
  ::v-deep .el-button--primary {
    background-color: #087f5b;
    border-color: #087f5b;
  }

  .light-swatch {
    background: #ecf5ff; // 仍是默认蓝的浅色阶 —— 漏改
    color: #409eff;
  }
}

// 完整色阶：交互态一起跟着走
.theme-demo--full {
  ::v-deep .el-button--primary {
    background-color: #087f5b;
    border-color: #087f5b;

    &:hover,
    &:focus {
      background-color: #0a9a6e;
      border-color: #0a9a6e;
    }
  }

  .light-swatch {
    background: #eef7f4;
    color: #087f5b;
  }
}

.theme-demo__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.theme-demo__row--light {
  padding: 10px 12px;
  border-radius: 4px;
  background: var(--surface-1);
}

.light-swatch {
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 12px;
  transition: background-color 0.2s, color 0.2s;
}

.color-scale {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.color-scale__item {
  flex: 1;
  min-width: 64px;
  padding: 8px 6px;
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  text-align: center;
  white-space: nowrap;
}

// 深色 / 浅色主题演示
.theme-surface {
  padding: 14px;
  border-radius: 6px;
  background: var(--surface);
  border: 1px solid var(--line);
  transition: background-color 0.2s, border-color 0.2s;

  // 浅色主题变量（按用途命名）
  --surface: #fff;
  --surface-flat: #fafbfc;
  --text-primary: #202b33;
  --text-secondary: #5c6872;
  --line: #e3e8ec;

  &[data-theme='dark'] {
    --surface: #1c242c;
    --surface-flat: #232d36;
    --text-primary: #e8edf0;
    --text-secondary: #b4c0c9;
    --line: #35434e;
  }
}

.theme-card {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding: 12px;
  border-radius: 6px;
  background: var(--surface-flat);
  border: 1px solid var(--line);
}

.theme-card__title {
  width: 100%;
  margin-bottom: 2px;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
}

.theme-field {
  width: 150px;

  @include bp-small {
    width: 120px;
  }
}

.theme-hint {
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.7;

  code {
    padding: 2px 6px;
    border-radius: 3px;
    background: rgba(128, 128, 128, 0.16);
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 11px;
  }
}

.theme-card--flat {
  margin-top: 10px;
  display: block;
}

.preview-empty {
  padding: 24px;
  color: #909399;
  font-size: 13px;
  text-align: center;
}

// ===== 对照表 =====
.table-card {
  margin: 14px 0;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  overflow: hidden;
}

.table-row {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);

  & + .table-row {
    border-top: 1px dashed #eef2f7;
  }

  @include bp-small {
    grid-template-columns: 84px minmax(0, 1fr);
  }
}

.table-key {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  background: var(--surface-1);
  color: #5a4f96;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.table-value {
  padding: 12px 14px;
  color: #42536a;
  font-size: 13px;
  line-height: 1.8;

  code {
    padding: 2px 6px;
    border-radius: 3px;
    background: #eef3fa;
    color: #315dcf;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
  }

  b {
    color: #1f2d3d;
  }

  @include bp-small {
    font-size: 12px;
  }
}

// ===== 职责分工表 =====
.duty-table {
  margin: 4px 0 14px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  overflow: hidden;
}

.duty-head,
.duty-row {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  align-items: center;

  @include bp-small {
    grid-template-columns: 130px minmax(0, 1fr);
  }
}

.duty-head {
  padding: 10px 14px;
  background: #f0eefc;
  color: #5a4f96;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.duty-row {
  padding: 12px 14px;
  border-top: 1px dashed #eef2f7;
  background: var(--surface-1);
}

.duty-where {
  color: #1f2d3d;
  font-size: 13px;
  font-weight: 600;
}

.duty-what {
  color: #667085;
  font-size: 13px;
  line-height: 1.75;

  @include bp-small {
    font-size: 12px;
  }
}

// ===== 要点列表 =====
.point-list {
  margin: 12px 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 0;
    color: #42536a;
    font-size: 14px;
    line-height: 1.75;

    @include bp-middle {
      font-size: 13px;
    }

    @include bp-small {
      font-size: 12px;
    }
  }

  code {
    padding: 2px 6px;
    border-radius: 3px;
    background: #eef3fa;
    color: #315dcf;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
  }

  strong {
    color: #1f2d3d;
  }
}

.point-icon {
  flex: none;
  margin-top: 5px;
  color: #52a552;
  font-size: 13px;
}

// ===== 提示条 =====
.tip-bar {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 14px;
  padding: 12px 14px;
  border-left: 3px solid #5a4f96;
  border-radius: 4px;
  background: #f7f5ff;
  color: #4a5a70;
  font-size: 13px;
  line-height: 1.75;

  code {
    padding: 2px 6px;
    border-radius: 3px;
    background: #eae7f8;
    color: #5a4f96;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
  }

  @include bp-middle {
    font-size: 12px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.tip-bar--warn {
  border-left-color: #d98b8b;
  background: #fdf6f6;
}

.tip-icon {
  flex: none;
  margin-top: 3px;
  color: #5a4f96;
}

.tip-bar--warn .tip-icon {
  color: #d98b8b;
}

// ===== 检查清单 =====
.check-list {
  margin: 0 0 16px;
  padding: 0;
  list-style: none;
}

.check-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dashed #eef2f7;

  &:last-child {
    border-bottom: none;
  }
}

.check-index {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  border-radius: 50%;
  background: #ecf5ff;
  color: #315dcf;
  font-size: 12px;
  font-weight: 600;
}

.check-text {
  color: #42536a;
  font-size: 14px;
  line-height: 1.75;

  @include bp-middle {
    font-size: 13px;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.final-note {
  padding-top: 14px;
  border-top: 1px dashed #e8eef6;
}

// ===== 右侧目录 =====
.doc-aside {
  position: sticky;
  top: 80px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.toc-card {
  padding: 16px 18px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  background: var(--surface-1);
  box-shadow: 0 4px 14px rgba(31, 45, 61, 0.06);
}

.toc-title {
  margin-bottom: 12px;
  color: #1f2d3d;
  font-size: 14px;
  font-weight: 600;

  @include bp-small {
    font-size: 13px;
  }
}

.toc-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.toc-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 5px;
  color: #667085;
  font-size: 13px;
  line-height: 1.5;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background: #f7f5ff;
    color: #5a4f96;
  }

  &.is-active {
    background: #f0eefc;
    color: #5a4f96;
    font-weight: 600;
  }

  @include bp-small {
    font-size: 12px;
  }
}

.toc-index {
  flex: none;
  color: #a8b6c8;
  font-size: 12px;
  font-weight: 500;
}

.fact-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #eef2f7;

  &:last-child {
    border-bottom: none;
  }

  span {
    color: #909399;
    font-size: 12px;
    white-space: nowrap;
  }

  strong {
    color: #42536a;
    font-size: 12px;
    font-weight: 600;
    text-align: right;
    word-break: break-all;
  }
}

// 参考项目卡片
.repo-link {
  display: inline-block;
  margin-bottom: 8px;
  color: #5a4f96;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
}

.aside-note--dark {
  margin-top: 0;
  color: #7a8797;
  font-size: 12px;
  line-height: 1.7;
}

.repo-file-list {
  margin: 12px 0 0;
  padding: 12px 0 0;
  border-top: 1px dashed #eef2f7;
  list-style: none;
  counter-reset: repo;

  li {
    position: relative;
    padding: 6px 0 6px 18px;
    counter-increment: repo;

    &::before {
      content: counter(repo) '.';
      position: absolute;
      left: 0;
      color: #a8b6c8;
      font-size: 12px;
    }
  }

  a {
    color: #667085;
    font-size: 12px;
    line-height: 1.6;
    text-decoration: none;

    &:hover {
      color: #5a4f96;
      text-decoration: underline;
    }
  }
}
</style>
